import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Button,
  InputGroup,
} from "reactstrap";
import "flatpickr/dist/themes/material_green.css";
import Flatpickr from "react-flatpickr";
import Breadcrumbs from "components/Common/Breadcrumb";
import TableContainerV1 from "components/Common/TableContainerV1";
import { getZoneWiseSalesReport } from "../../../helpers/UAM/report.helper"
import * as XLSX from 'xlsx';
import monthSelectPlugin from "flatpickr/dist/plugins/monthSelect";
import 'flatpickr/dist/plugins/monthSelect/style.css';
import { formatNumberWithCommas } from "common/utility";

const ZoneSalesReport = props => {

  //meta title
  document.title = "Zone Sales Report | DMS";
  const [zoneWiseSalesReport, setZoneWiseSalesReport] = useState([]);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageCount: 0, totalRecords: 0, })
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchValue, setSearchValue] = useState("");
  const [columnId, setColumnId] = useState("id");
  const [filters, setFilters] = useState(null);
  const [sortingOrderStatus, setSortingOrderStatus] = useState("desc");
  const [sortingOptions, setSortingOptions] = useState({ column: "", isAsc: false, optionalColumn: ['zoneName', 'targetSaleInLac', 'actualSaleInLac', 'difference', 'performance','percentageDifference'], });
  const flatpickrRef = useRef(null);
  const [totalData, setTotalData] = useState('');

  const columns = useMemo(
    () => [

      {
        Header: 'Zone',
        accessor: 'zoneName',
        filterable: true,
        Cell: (cellProps) => {
          return cellProps.value ? cellProps.value : "";
        }
      },
      {
        Header: 'Target Sale',
        accessor: 'targetSaleInLac',
        filterable: true,
        Cell: (cellProps) => {
          return cellProps.value ? formatNumberWithCommas(cellProps.value) : "";
          ;
        }
      },
      {
        Header: 'Actual Sale',
        accessor: 'actualSaleInLac',
        filterable: true,
        Cell: (cellProps) => {
          return cellProps.value ? formatNumberWithCommas(cellProps.value) : "";
        }
      },
      {
        Header: 'Difference',
        accessor: 'difference',
        filterable: true,
        Cell: (cellProps) => {
          return cellProps.value ? formatNumberWithCommas(cellProps.value) : "";
        }
      },
      {
        Header: 'Difference (%)',
        accessor: 'percentageDifference',
        filterable: true,
        Cell: (cellProps) => {
          const { targetSaleInLac, actualSaleInLac } = cellProps.row.original;
          if (targetSaleInLac === 0 || targetSaleInLac === null || targetSaleInLac === undefined) {
            return "";
          }
          const percentageDifference = ((targetSaleInLac - actualSaleInLac) / targetSaleInLac) * 100;
          return isNaN(percentageDifference) ? "" : `${percentageDifference.toFixed(2)}%`;
        }
      },
      {
        Header: 'Performance ',
        accessor: 'performance',
        filterable: true,
        Cell: (cellProps) => {
          let value = cellProps.row.original.difference
          if (value === null || value === undefined) {
            return "";
          }

          const iconClass = value < 0 ? 'bx bxs-downvote' : 'bx bxs-upvote';
          const color = value < 0 ? 'red' : 'green';

          return (
            <span style={{ color }}>
              <i className={iconClass}></i>
            </span>
          );

        }
      },
    ],
  );

  useEffect(() => {
    fetchZoneWiseSalesReport({ page: 1, limit: pageSize, searchKey: '' })
  }, [])

  const fetchZoneWiseSalesReport = (data) => {
    getZoneWiseSalesReport(data)
      .then((response) => {
        if (response.status && response.code == 200) {
          setZoneWiseSalesReport(response.data.data);
          setTotalData(calculateZoneTotals(response.data.data))
          const { totalItems, totalPages, currentPage } = response.data
          setPagination({
            pageIndex: currentPage,
            pageCount: totalPages,
            totalRecords: totalItems + 1,
          })
        } else {
          setPagination({ totalRecords: 0 })
          setZoneWiseSalesReport([])
          setPagination({
            pageIndex: 0,
            pageCount: 0,
            totalRecords: 0,
          })
        }

      })
      .catch((err) => { console.log(err) })
  }

  const handlePageChange = (page, pageSize, searchKey = searchValue, columnName = columnId, sortingOrder = sortingOrderStatus, filter = filters) => {
    setPageSize(pageSize);
    setCurrentPage(page)
    fetchZoneWiseSalesReport({ page, limit: pageSize, searchKey, columnName, sortingOrder, filters: filter })
  };

  const handleSortColumn = (option) => {
    setSortingOptions(option);
    const columnName = option.column;
    setColumnId(columnName);
    const sortingOrder = option.isAsc ? "asc" : "desc";
    setSortingOrderStatus(sortingOrder);
    handlePageChange(currentPage, pageSize, searchValue, columnName, sortingOrder, filters);
  };

  const handleSearchFilter = (value, pageSize) => {
    const newValue = value !== undefined ? value : "";
    setSearchValue(newValue);
    handlePageChange(1, pageSize, newValue);

  };

  const handleClearSearch = () => {
    setSearchValue("");
    setFilters(null)
    if (flatpickrRef.current) {
      flatpickrRef.current.flatpickr.clear();
      flatpickrRef.current.flatpickr.close();
    }
    handlePageChange(1, pageSize, "", 'id', 'desc', null, null);
  };

  const handleDateFilter = () => {
    handlePageChange(1, pageSize, searchValue, columnId, sortingOrderStatus, filters)
  }

  const handleDateChange = (selectedDates) => {
    if (selectedDates.length === 2) {
      setFilters({
        startDate: selectedDates[0],
        endDate: selectedDates[1],
      })
    }
  };

  const downloadExcel = () => {
    let dataWithModifiedCol = zoneWiseSalesReport.map(report => {
      let percentageValue = ''
      if (report.targetSaleInLac === 0 || report.targetSaleInLac === null || report.targetSaleInLac === undefined) {
        percentageValue = "";
      } else {
        const percentageDifference = ((report.targetSaleInLac - report.actualSaleInLac) / report.targetSaleInLac) * 100;
        percentageValue = isNaN(percentageDifference) ? "" : `${percentageDifference.toFixed(2)}%`;
      }

      let newReport = {
        "Zone": report.zoneName,
        "Target Sale": formatNumberWithCommas(report.targetSaleInLac),
        "Actual Sale": formatNumberWithCommas(report.actualSaleInLac),
        "Difference": formatNumberWithCommas(report.difference),
        "Difference (%)": percentageValue,
      }
      return newReport
    })
    const totalTargetSale = zoneWiseSalesReport.reduce((sum, report) => sum + report.targetSaleInLac, 0);
    const totalActualSale = zoneWiseSalesReport.reduce((sum, report) => sum + report.actualSaleInLac, 0);
    const totalDifference = zoneWiseSalesReport.reduce((sum, report) => sum + report.difference, 0);
    let totalPercentageValue = ''
    if (totalTargetSale === 0 || totalTargetSale === null || totalTargetSale === undefined) {
      totalPercentageValue = "";
    } else {
      const percentageDifference = ((totalTargetSale - totalActualSale) / totalTargetSale) * 100;
      totalPercentageValue = isNaN(percentageDifference) ? "" : `${percentageDifference.toFixed(2)}%`;
    }
    dataWithModifiedCol.push({
      "Zone": "Total",
      "Target Sale": formatNumberWithCommas(totalTargetSale),
      "Actual Sale": formatNumberWithCommas(totalActualSale),
      "Difference": formatNumberWithCommas(totalDifference),
      "Difference (%)": totalPercentageValue,
    });

    const workbook = XLSX.utils.book_new();
    const sheetData = XLSX.utils.json_to_sheet(dataWithModifiedCol);
    // Add the sheet to the workbook
    XLSX.utils.book_append_sheet(workbook, sheetData, 'ZoneWiseSalesReport');
    // Save the workbook as an Excel file
    XLSX.writeFile(workbook, 'Zone_Wise_Sales_Report.xlsx');
  };

  const customeFilterSection = (
    <div className="d-flex align-items-center justify-content-start">
      <InputGroup style={{ minWidth: '250px' }}>
        <Flatpickr
          ref={flatpickrRef}
          className="form-control"
          placeholder="Start Date - End Date"
          options={{
            mode: 'range',
            dateFormat: 'd-m-Y',
          }}
          value={filters?.startDate && filters?.endDate ? [filters?.startDate, filters?.endDate] : []}
          onChange={handleDateChange}
        />
        <Button variant="primary"
          onClick={handleDateFilter}
          style={{ backgroundColor: "#344fa0", border: "none" }}>
          Go
        </Button>
      </InputGroup>
    </div>
  )

  const buttonSection = (
    <div className="text-sm-end">

      {zoneWiseSalesReport?.length > 0 && (<Button
        type="button"
        color="success"
        className="btn-rounded mb-2 me-2"
        onClick={downloadExcel}
      >
        <i className="bx bx-archive-in me-1" />
        {'Download'}
      </Button>
)}
    </div>);

  const calculateZoneTotals = (data) => {
    let totalTargetSaleInLac = 0;
    let totalActualSaleInLac = 0;
    let totalDifference = 0;
    let totalPercentageDiff = ''
    data.forEach(item => {
      totalTargetSaleInLac += item.targetSaleInLac || 0;
      totalActualSaleInLac += item.actualSaleInLac || 0;
      totalDifference += item.difference || 0;
    });
    if (totalTargetSaleInLac === 0 || totalTargetSaleInLac === null || totalTargetSaleInLac === undefined) {
      totalPercentageDiff = "";
    } else {
      const percentageDifference = ((totalTargetSaleInLac - totalActualSaleInLac) / totalTargetSaleInLac) * 100;
      totalPercentageDiff = isNaN(percentageDifference) ? "" : `${percentageDifference.toFixed(2)}%`;
    }

    const iconClass = totalDifference < 0 ? 'bx bxs-downvote' : 'bx bxs-upvote';
    const color = totalDifference < 0 ? 'red' : 'green';

    return (<tr>
      <td className="table-row-odd font-size-14" style={{ fontWeight: '600' }}>{'Total'}</td>
      <td className="table-row-odd font-size-14" style={{ fontWeight: '600' }}>{formatNumberWithCommas(totalTargetSaleInLac)}</td>
      <td className="table-row-odd font-size-14" style={{ fontWeight: '600' }}>{formatNumberWithCommas(totalActualSaleInLac)}</td>
      <td className="table-row-odd font-size-14" style={{ fontWeight: '600' }}>{formatNumberWithCommas(totalDifference)}</td>
      <td className="table-row-odd font-size-14" style={{ fontWeight: '600' }}>{totalPercentageDiff}</td>
      <td className="table-row-odd font-size-14" style={{ fontWeight: '600' }}>
        {<span style={{ color }}>
          <i className={iconClass}></i>
        </span>}
      </td>
    </tr>)
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Reports" breadcrumbItem="Zone Wise Sales Report" />
        <Row>
          <Col xs="12">
            <Card>
              <CardBody>
                <TableContainerV1
                  columns={columns}
                  data={zoneWiseSalesReport}
                  pagination={pagination}
                  handlePageChange={handlePageChange}
                  searchHelperText="Search by Zone"
                  handleSearchFilter={handleSearchFilter}
                  customFilterSection={customeFilterSection}
                  customSection={buttonSection}
                  sortOptions={sortingOptions}
                  handleSortColumn={handleSortColumn}
                  handleClearSearch={handleClearSearch}
                  isTotal={true}
                  TotalData={totalData}
                  className="custom-header-css"
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default ZoneSalesReport;
